import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import type { SearchResultJobV5 } from '@seek/chalice-types';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';

import useJobDetail from 'src/components/SearchResultPage/SplitView/JobDetail/useJobDetail';
import {
  persistJobUrl,
  removePersistedJobUrl,
} from 'src/components/SearchResultPage/SplitView/helpers';
import { useAppConfig } from 'src/config/appConfig';
import type { HubbleJDVEventTriggerTag } from 'src/hooks/trackJobDetailsLoaded';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import {
  fetchUnifiedJob,
  fetchUnifiedJobPersonalised,
  setJobDetailsCorrelationId,
} from 'src/store/jobdetails';
import { setSelectedJobIdAction } from 'src/store/results';
import {
  selectAuthenticated,
  selectJobDetailsResult,
  selectSelectedJobId,
  selectSessionId,
  selectXRealIp,
} from 'src/store/selectors';
import { useMelwaysLocale, useMelwaysZone } from 'src/utils/melwaysHelper';

import { useDispatch, useSelector } from '../store/react';

import useShouldFetchJobDetails from './useShouldFetchJobDetails';

const mapEventTriggerToJobListingPosition = (
  trigger: HubbleJDVEventTriggerTag,
) => {
  switch (trigger) {
    case 'pre_selected':
      return -1;
    case 'auto_display':
      return 0;
    default:
      return undefined;
  }
};

const useFetchJobDetails = (job?: SearchResultJobV5) => {
  const dispatch = useDispatch();
  const analyticsFacade = useAnalyticsFacade();
  const currentJobDetails = useSelector(selectJobDetailsResult);
  const jobseekerSessionId = useSelector(selectSessionId);
  const authenticated = useSelector(selectAuthenticated);
  const { country: countryCode } = useAppConfig();
  const { language: languageCode } = useMelwaysInfo();
  const locale = useMelwaysLocale();
  const zone = useMelwaysZone();
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const shouldFetchJobDetails = useShouldFetchJobDetails(job);
  const ipAddress = useSelector(selectXRealIp);

  const fetchJobDetails = useCallback(() => {
    // Spltiview JDV is rendered client-side
    if (ENV.SERVER || !job) {
      return Promise.resolve(undefined);
    }

    const jobId = job.id.toString();

    dispatch(
      fetchUnifiedJob({
        apolloClient,
        analyticsFacade,
        jobId,
        jobseekerSessionId,
        zone,
        locale,
        languageCode,
        shouldThrowError: false,
        countryCode,
        xRealIp: ipAddress,
      }),
    );

    if (authenticated) {
      dispatch(
        fetchUnifiedJobPersonalised({
          apolloClient,
          jobId,
          jobseekerSessionId,
          languageCode,
          locale,
          zone,
          xRealIp: ipAddress,
        }),
      );
    }
  }, [
    job,
    dispatch,
    apolloClient,
    analyticsFacade,
    jobseekerSessionId,
    zone,
    locale,
    languageCode,
    countryCode,
    authenticated,
    ipAddress,
  ]);

  useEffect(() => {
    if (shouldFetchJobDetails) {
      dispatch(setJobDetailsCorrelationId(uuid()));
      fetchJobDetails();
    }
  }, [shouldFetchJobDetails, fetchJobDetails, dispatch]);

  return {
    currentJobDetails,
    fetchJobDetails,
  };
};

export const useSplitView = () => {
  const [selectedJob, setSelectedJob] = useState<SearchResultJobV5>();
  const dispatch = useDispatch();
  const [displayEventTrigger, setDisplayEventTrigger] =
    useState<HubbleJDVEventTriggerTag>('default');
  const { currentJobDetails, fetchJobDetails } =
    useFetchJobDetails(selectedJob);
  const selectedJobId = useSelector(selectSelectedJobId);

  const setSelectedJobAndDisplayEventTrigger = (
    job?: SearchResultJobV5,
    eventTrigger?: HubbleJDVEventTriggerTag,
  ) => {
    setSelectedJob(job);
    metrics.count('pageload');

    if (job) {
      persistJobUrl({ job });
    } else if (selectedJobId) {
      dispatch(setSelectedJobIdAction(undefined));
      removePersistedJobUrl();
    }
    setDisplayEventTrigger(eventTrigger ?? 'default');
  };

  // Apollo's loading state is not firing correctly, so we need to use the store state instead
  // Will be removed once Apollo's loading state is fixed
  const { isLoading } = useJobDetail();

  return {
    selectedJob,
    setSelectedJob: setSelectedJobAndDisplayEventTrigger,
    clearSelectedJob: () => {
      setSelectedJob(undefined);
      dispatch(setSelectedJobIdAction(undefined));
    },
    selectedJobPosition:
      mapEventTriggerToJobListingPosition(displayEventTrigger) ??
      Number(selectedJob?.solMetadata.sectionRank),
    eventTrigger: displayEventTrigger,
    currentJobDetails,
    fetchJobDetails,
    isLoading,
  };
};

type SplitviewContextType = ReturnType<typeof useSplitView>;

const SplitViewContext = createContext<SplitviewContextType>(
  {} as SplitviewContextType,
);

export const SplitViewContextProvider = SplitViewContext.Provider;

export const useSplitViewContext = () => useContext(SplitViewContext);
